import { FC, useEffect, useState } from 'react'

import { Box, Divider, Flex, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useReadLocalStorage } from 'usehooks-ts'

import { FeedbackPopover } from '@core/components/Sidebar/FeedbackPopover'
import {
    AbsenceIcon,
    AbsenceIconHovered,
    AgreementsIcon,
    AgreementsIconHovered,
    CalendarHoverIcon,
    CalendarIcon,
    HomeIcon,
    HomeIconHovered,
    LoggedWorkIcon,
    LoggedWorkIconHovered,
    LogworkIcon,
    LogworkIconHovered,
    NewsIcon,
    NewsIconHovered,
    PayslipsIconHovered,
    RequestIcon,
    RequestIconHovered,
    SalaryIcon,
    SalaryIconHovered,
} from '@core/components/Sidebar/Icons'
import { NavItem } from '@core/components/Sidebar/NavItem'
import { IconFileDollar, IconFileDollarSolid } from '../v2/Icon'

interface Props {
    isFixed?: boolean
}

// Add global styles for animations
const sidebarStyles = `
    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`

export const Sidebar: FC<Props> = (): JSX.Element => {
    useState(() => {
        if (typeof window === 'undefined') return null
        if (window.matchMedia('(max-width: 768px)').matches) {
            if (window.localStorage.getItem('isSidebarOpened') === 'true') {
                window.localStorage.setItem('isSidebarOpened', 'false')
            }
        }
        return null
    })

    const isSidebarOpened = useReadLocalStorage('isSidebarOpened') as boolean | null
    const employeeMenu = window.employeeData?.menu
    const subId = window.employeeData?.profile?.subscriptionId
        ? +window.employeeData.profile.subscriptionId
        : null
    const { t } = useTranslation()
    const { pathname } = useLocation()

    type NavItemType = {
        icon: JSX.Element
        hoveredIcon?: JSX.Element
        label: string
        active: boolean
        link: string
        count?: number
    }

    const pendingRequestsCount = window.employeeData?.menu?.countRequests ?? 0
    const pendingAgreementsCount = window.employeeData?.menu?.countAgreements ?? 0

    const general = [
        {
            icon: <HomeIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <HomeIconHovered boxSize={'20px'} />,
            label: t('201') || 'Home',
            active: pathname === '/employee/homescreen' || pathname === '/homescreen',
            link: process.env.ENV === 'local' ? '/homescreen' : '/employee/homescreen',
        },
        employeeMenu?.newsfeed && {
            icon: <NewsIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <NewsIconHovered boxSize={'20px'} />,
            label: t('5137') || 'News',
            active: pathname === '/newsfeed' || pathname.startsWith('/newsfeed/'),
            link: '/newsfeed',
        },
        employeeMenu?.agreements && {
            icon: <AgreementsIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <AgreementsIconHovered boxSize={'20px'} />,
            label: t('3339') || 'Agreements',
            active: pathname === '/profile/agreement',
            link: '/profile/agreement',
            count: pendingAgreementsCount,
        },
    ].filter(Boolean) as NavItemType[]

    const work = [
        {
            icon: <LogworkIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <LogworkIconHovered boxSize={'20px'} />,
            label: t('532') || 'Log work',
            active: pathname === '/employee/log-work',
            link: '/employee/log-work',
        },
        {
            icon: <AbsenceIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <AbsenceIconHovered boxSize={'20px'} />,
            label: t('769') || 'Absence',
            active: pathname === '/employee/absence',
            link: '/employee/absence',
        },
        {
            icon: <LoggedWorkIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <LoggedWorkIconHovered boxSize={'20px'} />,
            label: t('531') || 'Logged work',
            active: pathname === '/employee/logged-work',
            link: '/employee/logged-work',
        },
        {
            icon: <RequestIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <RequestIconHovered boxSize={'20px'} />,
            label: t('3515') || 'Requests',
            active: pathname === '/employee/requests',
            link: '/employee/requests',
            count: pendingRequestsCount,
        },
        // (envId === '1' || envId === '2') &&
        {
            icon: <CalendarIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <CalendarHoverIcon boxSize={'20px'} />,
            label: t('2106') || 'Calendar',
            active: pathname === '/employee/calendar',
            link: '/employee/calendar',
        }
    ].filter(Boolean) as NavItemType[]

    const additional = [
        employeeMenu?.payroll && {
            icon: <SalaryIcon boxSize={'20px'} color={'#585858'} />,
            hoveredIcon: <SalaryIconHovered boxSize={'20px'} />,
            label: t('329') || 'Salary',
            active: pathname === '/employee/paycheck',
            link: '/employee/paycheck',
        },
        (subId === 617 || subId === 593) && {
            icon: <IconFileDollar size={20} color={'#58585B'} />,
            hoveredIcon: <PayslipsIconHovered boxSize={'20px'} />,
            label: 'Payslips',
            active: pathname === '/employee/payslips',
            link: '/employee/payslips',
        },

    ].filter(Boolean) as NavItemType[]

    const handleOverlayClick = () => {
        const mediaQuery = window.matchMedia('(max-width: 768px)')
        const isMobileDevice = mediaQuery.matches

        if (isMobileDevice && sidebarOpened) {
            localStorage.setItem('isSidebarOpened', 'false')
            window.dispatchEvent(
                new CustomEvent('localStorageChange', {
                    detail: { key: 'isSidebarOpened', value: false },
                })
            )
            window.dispatchEvent(new StorageEvent('local-storage', { key: 'isSidebarOpened' }))
        }
    }

    const sidebarOpened = isSidebarOpened ?? true

    // Disable scroll when sidebar is open on mobile
    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 768px)')
        const isMobileDevice = mediaQuery.matches

        if (!isMobileDevice) return

        if (sidebarOpened) {
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.body.style.overflow = ''
        }
    }, [sidebarOpened])

    // Add styles to document head
    useEffect(() => {
        const styleElement = document.createElement('style')
        styleElement.textContent = sidebarStyles
        document.head.appendChild(styleElement)

        return () => {
            document.head.removeChild(styleElement)
        }
    }, [])

    return (
        <>
            <Box
                position="fixed"
                top="0px"
                left="0"
                right="0"
                bottom="0"
                bg="rgba(0, 0, 0, 0.5)"
                zIndex={1002}
                onClick={handleOverlayClick}
                opacity={{
                    base: sidebarOpened ? 1 : 0,
                    md: 0,
                }}
                visibility={{
                    base: sidebarOpened ? 'visible' : 'hidden',
                    md: 'hidden',
                }}
                transition="opacity 0.25s ease-in-out, visibility 0.25s ease-in-out"
                display={{ base: 'block', md: 'none' }}
            />
            <Box
                display={{ base: 'none', md: 'block' }}
                w={{ md: !sidebarOpened ? '56px' : '240px' }}
                flexShrink={0}
                bg={'#FFFFFF'}
                transition={'width ease-in-out .25s'}
                aria-hidden={'true'}
            />
            <Flex
                bg={'#FFFFFF'}
                pos={{
                    base: 'absolute',
                    md: 'fixed',
                }}
                top={'56px'}
                left={0}
                maxH={'calc(100dvh - 56px)'}
                h={'calc(100dvh - 56px)'}
                w={{
                    base: !sidebarOpened ? '0px' : '100vw',
                    md: !sidebarOpened ? '56px' : '240px',
                }}
                flexDir={'column'}
                transition={'all ease-in-out .25s'}
                transform={{
                    base: !sidebarOpened ? 'translateX(-100%)' : 'translateX(0)',
                    md: 'translateX(0)',
                }}
                px={{
                    base: !sidebarOpened ? '0px' : '8px',
                    md: '8px',
                }}
                pb={{
                    base: 'max(16px, env(safe-area-inset-bottom))',
                    md: '0',
                }}
                pt={{ base: '16px', md: '0px' }}
                overflowX={'hidden'}
                borderRight={'1px solid'}
                borderColor={'#EAEAEB'}
                as={'aside'}
                flexShrink={0}
                zIndex={1003}
                visibility={{
                    base: !sidebarOpened ? 'hidden' : 'visible',
                    md: 'visible',
                }}
            >
                <Box mt={{ base: sidebarOpened ? '0px' : '16px', md: '16px' }}>
                    <Flex flexDirection={'column'} gap={'2px'}>
                        {general.map((item, index) => (
                            <NavItem
                                key={index}
                                icon={item.icon}
                                hoveredIcon={item.hoveredIcon}
                                label={item.label}
                                active={item.active}
                                sidebarClosed={!sidebarOpened}
                                onClick={() => {
                                    location.href = item.link
                                }}
                                count={item.count}
                            />
                        ))}
                    </Flex>
                </Box>
                <Box>
                    <Flex
                        alignItems={'center'}
                        h={{
                            base: '40px',
                            md: sidebarOpened ? '40px' : '0px',
                        }}
                        opacity={{
                            base: '1',
                            md: !sidebarOpened ? '0' : '1',
                        }}
                        visibility={{
                            base: 'visible',
                            md: !sidebarOpened ? 'hidden' : 'visible',
                        }}
                        px={'6px'}
                        transition={'opacity .2s ease, height .2s ease'}
                        overflow={'hidden'}
                    >
                        <Text color={'#0C0C0D'} fontSize={'xs'} fontWeight={'semibold'} pl={'5px'}>
                            {t('2963') || 'Work'}
                        </Text>
                    </Flex>
                    <Divider
                        my={!sidebarOpened ? '16px' : '0px'}
                        mx={'-8px'}
                        w={'56px'}
                        bg={'#EAEAEB'}
                        color={'#EAEAEB'}
                        opacity={sidebarOpened ? 0 : 1}
                        visibility={sidebarOpened ? 'hidden' : 'visible'}
                        transition={'opacity .2s ease, margin .2s ease'}
                        display={{ base: 'none', md: 'block' }}
                    />
                    <Flex flexDirection={'column'} gap={'2px'}>
                        {work.map((item, index) => (
                            <NavItem
                                key={index}
                                icon={item.icon}
                                hoveredIcon={item.hoveredIcon}
                                label={item.label}
                                active={item.active}
                                sidebarClosed={!sidebarOpened}
                                onClick={() => {
                                    handleOverlayClick()
                                    location.href = item.link
                                }}
                                count={item.count}
                            />
                        ))}
                    </Flex>
                    <Divider
                        my={!sidebarOpened ? '16px' : '0px'}
                        mx={'-8px'}
                        w={'56px'}
                        bg={'#EAEAEB'}
                        color={'#EAEAEB'}
                        opacity={sidebarOpened ? 0 : 1}
                        visibility={sidebarOpened ? 'hidden' : 'visible'}
                        transition={'opacity .2s ease, margin .2s ease'}
                        display={{ base: 'none', md: 'block' }}
                    />
                    <Flex flexDirection={'column'} gap={'2px'}>
                        {additional.map((item, index) => (
                            <NavItem
                                key={index}
                                icon={item.icon}
                                hoveredIcon={item.hoveredIcon}
                                label={item.label}
                                active={item.active}
                                sidebarClosed={!sidebarOpened}
                                onClick={() => {
                                    handleOverlayClick()
                                    location.href = item.link
                                }}
                                count={item.count}
                            />
                        ))}
                    </Flex>
                </Box>
                <FeedbackPopover mt={'auto'} mb={'24px'} onCloseSidebar={handleOverlayClick} />
            </Flex>
        </>
    )
}
